import { useEffect, useMemo, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isInStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const dismissedAt = localStorage.getItem("pwa-install-dismissed-at");
    if (dismissedAt) {
      const diff = Date.now() - Number(dismissedAt);
      const oneDay = 24 * 60 * 60 * 1000;
      if (diff < oneDay) {
        setDismissed(true);
      }
    }

    if (isInStandaloneMode()) {
      setInstalled(true);
    }

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setShowIosHelp(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const canShow = useMemo(() => {
    if (installed || dismissed) return false;
    if (deferredPrompt) return true;
    if (isIos() && !isInStandaloneMode()) return true;
    return false;
  }, [installed, dismissed, deferredPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice.outcome === "accepted") {
        setDeferredPrompt(null);
      }
      return;
    }

    if (isIos()) {
      setShowIosHelp(true);
    }
  };

  const handleClose = () => {
    localStorage.setItem("pwa-install-dismissed-at", String(Date.now()));
    setDismissed(true);
  };

  if (!canShow && !window.location.search.includes("pwa-test=1")) return null;

  return (
    <div className="fixed inset-x-0 bottom-4 z-[9999] px-4">
      <div className="mx-auto max-w-md rounded-[24px] border border-zinc-200 bg-white/95 shadow-2xl backdrop-blur">
        <div className="flex items-start gap-3 p-4">
          <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FE5668]/10">
            <Smartphone className="h-5 w-5 text-[#FE5668]" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-zinc-900">
              Instale o app da AllePets
            </div>

            <p className="mt-1 text-sm leading-5 text-zinc-600">
              Acesse mais rápido, direto da tela inicial do seu celular.
            </p>

            {showIosHelp ? (
              <div className="mt-3 rounded-2xl bg-zinc-50 p-3 text-sm text-zinc-700">
                No iPhone, toque em <strong>Compartilhar</strong> e depois em{" "}
                <strong>Adicionar à Tela de Início</strong>.
              </div>
            ) : (
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleInstall}
                  className="inline-flex items-center gap-2 rounded-full bg-[#FE5668] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  <Download className="h-4 w-4" />
                  Instalar
                </button>

                <button
                  type="button"
                  onClick={handleClose}
                  className="inline-flex items-center rounded-full px-3 py-2 text-sm font-medium text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700"
                >
                  Agora não
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}